import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { WebsiteProvider, useWebsite } from '../../contexts/WebsiteContext';
import {
  DashboardChromeProvider,
  useDashboardChrome,
} from '../../contexts/DashboardChromeContext';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useAdaptivePolling } from '../../hooks/useAdaptivePolling';
import { dropdownVariants, backdropVariants } from '../../utils/animations';
import { leadService } from '../../services/leadService';
import { billingService } from '../../services/billingService';
import { MaterialAvatar, OfflineIndicator } from '../ui';
import { PLAN_NAMES } from '@foundlio/pricing';
import NotificationBell from './NotificationBell';
import VerificationBanner from './VerificationBanner';
import SearchButton from './SearchButton';
import SearchModal from './SearchModal';
import KeyboardShortcutHelp from './KeyboardShortcutHelp';
import WebsiteSwitcher from './WebsiteSwitcher';
import SectionErrorBoundary from '../SectionErrorBoundary';
import { getPlanFeatures, normalizePlan } from '../../lib/planFeatures';

// Map plan types (including legacy ids) to their client-facing display name.
const getPlanDisplayName = (planType?: string | null): string =>
  PLAN_NAMES[normalizePlan(planType)] ?? 'Site';

type NavItem = {
  name: string;
  href: string;
  icon: React.ReactNode;
  locked?: boolean;
};
type NavGroup = { label: string | null; items: NavItem[] };

// The account destinations reachable from the user menu. Everything else in
// /dashboard/account is one click further, from the account sidebar.
const userMenuItems: NavItem[] = [
  {
    name: 'Profile',
    href: '/dashboard/account/profile',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={1.75}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
        />
      </svg>
    ),
  },
  {
    name: 'Billing',
    href: '/dashboard/account/billing',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={1.75}
      >
        <rect
          x="2"
          y="5"
          width="20"
          height="14"
          rx="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2 10h20" />
      </svg>
    ),
  },
  {
    name: 'Notifications',
    href: '/dashboard/account/notifications',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={1.75}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
        />
      </svg>
    ),
  },
];

const DashboardLayout: React.FC = () => {
  return (
    <WebsiteProvider>
      <DashboardChromeProvider>
        <DashboardLayoutInner />
      </DashboardChromeProvider>
    </WebsiteProvider>
  );
};

const DashboardLayoutInner: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * The same chrome renders the public tour at /demo (src/demo/), which has no
   * account behind it. Nav destinations are therefore rebased onto whichever
   * prefix mounted us, and the fixed chrome drops below the demo marker bar.
   */
  const isDemo =
    location.pathname === '/demo' || location.pathname.startsWith('/demo/');
  const rebase = (href: string) =>
    isDemo ? `/demo${href.slice('/dashboard'.length)}` : href;
  const { user, logout } = useAuth();
  const {
    websites: websitesData,
    isSingleWebsite,
    selectedWebsite,
  } = useWebsite();
  const { chromeHidden } = useDashboardChrome();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);

  const pollingInterval = useAdaptivePolling();

  // Keyboard shortcuts
  const onSearch = useCallback(() => setSearchOpen(true), []);
  const onHelp = useCallback(() => setShortcutHelpOpen(true), []);
  useKeyboardShortcuts({ onSearch, onHelp });

  // Cmd+K / Ctrl+K for search; Escape closes the user menu
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setUserMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fetch unread LEAD count for the Leads nav badge (adaptive polling).
  // Driven by CRMLead.viewedAt (see fnd-yb9) — a lead counts as unread until
  // its detail modal has actually been opened, not by a notification-bell
  // flag that got wiped wholesale on every visit to /leads regardless of
  // whether anything was actually read.
  const { data: unreadLeadCount } = useQuery({
    queryKey: ['leads-unread-count'],
    queryFn: () => leadService.getUnreadCount(),
    refetchInterval: pollingInterval,
    staleTime: 10000,
  });

  // Fetch subscription for plan display
  const { data: subscriptionData } = useQuery({
    queryKey: ['subscription-for-nav'],
    queryFn: async () => {
      try {
        return await billingService.getSubscription();
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const planName = subscriptionData
    ? getPlanDisplayName(subscriptionData.planType)
    : 'Starter';
  const features = getPlanFeatures(subscriptionData?.planType);

  const handleLogout = async () => {
    await logout();
    navigate(isDemo ? '/demo' : '/');
  };

  // Nav is grouped under monospace section labels — the same device the
  // marketing site uses to caption a block of content.
  const allGroups: NavGroup[] = [
    {
      label: null,
      items: [
        {
          name: 'Dashboard',
          href: '/dashboard',
          icon: (
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
              />
            </svg>
          ),
        },
        {
          name: 'Websites',
          href: '/dashboard/websites',
          icon: (
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.5 12h17M12 3.5c2 2.5 3.5 5.2 3.5 8.5s-1.5 6-3.5 8.5c-2-2.5-3.5-5.2-3.5-8.5s1.5-6 3.5-8.5z"
              />
            </svg>
          ),
        },
      ],
    },
    {
      label: 'Performance',
      items: [
        {
          name: 'Analytics',
          href: '/dashboard/analytics',
          icon: (
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941"
              />
            </svg>
          ),
        },
        {
          name: 'Search Keywords',
          href: '/dashboard/seo',
          icon: (
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM10.5 7.5v6m3-3h-6"
              />
            </svg>
          ),
        },
        {
          name: 'Reviews',
          href: '/dashboard/reviews',
          icon: (
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
              />
            </svg>
          ),
        },
      ],
    },
    {
      label: 'Customers',
      items: [
        {
          name: 'Leads',
          href: '/dashboard/leads',
          icon: (
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
              />
            </svg>
          ),
        },
        {
          name: 'Bookings',
          href: '/dashboard/bookings',
          icon: (
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
              />
            </svg>
          ),
        },
      ],
    },
    {
      label: 'Content',
      items: [
        {
          name: 'Content Plan',
          href: '/dashboard/content-plan',
          icon: (
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
              />
            </svg>
          ),
        },
        {
          name: 'Print Kit',
          href: '/dashboard/print-kit',
          icon: (
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z"
              />
            </svg>
          ),
        },
      ],
    },
  ];

  // Filter nav by single-website state — "Websites" is hidden for single-website
  // users (Dashboard becomes their hub). Plan-gated destinations stay visible and
  // are marked instead: the page itself makes the case for the upgrade, and a
  // nav item that silently vanishes can't. Groups left empty disappear.
  const navGroups = allGroups
    .map(group => ({
      ...group,
      items: group.items
        .filter(item => !(isSingleWebsite && item.name === 'Websites'))
        .map(item =>
          item.href === '/dashboard/seo' && !features.seo
            ? { ...item, locked: true }
            : item
        )
        .map(item => ({ ...item, href: rebase(item.href) })),
    }))
    .filter(group => group.items.length > 0);

  const isActive = (href: string) => {
    const home = rebase('/dashboard');
    const websites = rebase('/dashboard/websites');
    if (href === home) {
      return location.pathname === home || location.pathname === `${home}/`;
    }
    if (href === websites) {
      return (
        location.pathname === websites ||
        location.pathname.startsWith(`${websites}/`)
      );
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-paper">
      {/* Mobile sidebar backdrop */}
      <AnimatePresence>
        {sidebarOpen && !chromeHidden && (
          <motion.div
            variants={backdropVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/*
        Sidebar. The one dark surface in the product — the site uses ink the
        same way, as a terminus rather than a theme. Everything on it is
        hairline-ruled white/10; there are no fills and no accent colour.
      */}
      <aside
        className={`
        fixed ${isDemo ? 'top-10 h-[calc(100%-2.5rem)]' : 'top-0 h-full'}
        left-0 z-50 w-64 bg-ink text-zinc-300
        transform transition-transform duration-200 ease-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${chromeHidden ? 'hidden' : ''}
      `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
            <Link to={rebase('/dashboard')} className="flex items-center gap-2 min-w-0">
              <img src="/foundlio-on-dark.svg" alt="" className="h-6 w-auto" />
              <span className="font-display font-bold text-white text-lg tracking-tight">
                Foundlio
              </span>
            </Link>
            <div className="hidden lg:flex items-center gap-1">
              <SearchButton onClick={() => setSearchOpen(true)} />
              <NotificationBell />
            </div>
          </div>

          {/* Website Switcher — multi-site users */}
          <WebsiteSwitcher />

          {/* Update Website — the single primary action, for single-site users.
              Hidden on the demo tour: the editor reads the site's real content
              bundle, which the fixture backend has nothing to stand in for. */}
          {!isDemo && websitesData && websitesData.length === 1 && websitesData[0] && (
            <div className="px-4 py-4 border-b border-white/10">
              <Link
                to={rebase(`/dashboard/websites/${websitesData[0].id}/edit`)}
                onClick={() => setSidebarOpen(false)}
                className="flex items-center justify-center gap-2 w-full rounded-full bg-white px-4 py-2.5 text-sm font-medium text-ink transition-transform duration-200 hover:scale-[1.02] active:scale-[0.99]"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.75}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.862 4.487l1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                  />
                </svg>
                Update website
              </Link>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-5">
            {navGroups.map((group, groupIndex) => (
              <div
                key={group.label ?? `group-${groupIndex}`}
                className={groupIndex > 0 ? 'mt-6' : ''}
              >
                {group.label && (
                  <div className="px-3 pb-2 font-mono text-[10px] uppercase tracking-eyebrow text-zinc-600">
                    {group.label}
                  </div>
                )}
                <div className="space-y-0.5">
                  {group.items.map(item => {
                    const active = isActive(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className="relative block"
                      >
                        <div
                          className={`
                            relative flex h-9 items-center gap-3 rounded-lg px-3 text-sm
                            transition-colors duration-150
                            ${
                              active
                                ? 'font-medium text-white'
                                : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                            }
                          `}
                        >
                          {active && (
                            <motion.div
                              layoutId="activeNavIndicator"
                              className="absolute inset-0 rounded-lg bg-white/10"
                              transition={{
                                type: 'spring',
                                stiffness: 500,
                                damping: 35,
                              }}
                            />
                          )}
                          <span className="relative z-10">{item.icon}</span>
                          <span className="relative z-10">{item.name}</span>
                          {item.name === 'Leads' &&
                            (unreadLeadCount ?? 0) > 0 && (
                              <span className="relative z-10 ml-auto grid place-items-center min-w-[20px] h-5 px-1.5 font-mono text-[10px] font-medium text-ink bg-white rounded-full">
                                {(unreadLeadCount ?? 0) > 99
                                  ? '99+'
                                  : unreadLeadCount}
                              </span>
                            )}
                          {/* Not-on-your-plan marker. A quiet chip, not a padlock —
                              the destination still opens, onto the upsell. */}
                          {item.locked && (
                            <span className="relative z-10 ml-auto inline-flex h-[18px] shrink-0 items-center whitespace-nowrap rounded-full border border-white/15 px-1.5 font-mono text-[9px] uppercase leading-none tracking-wider text-zinc-500">
                              {PLAN_NAMES.professional}
                            </span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Visit the live site */}
          <div className="px-3 py-2 border-t border-white/10">
            <a
              href={
                selectedWebsite?.currentActiveUrl ||
                import.meta.env.VITE_MAIN_WEBSITE_URL ||
                '/'
              }
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-zinc-400 transition-colors hover:bg-white/5 hover:text-white"
            >
              <svg
                className="w-[18px] h-[18px]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.75}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                />
              </svg>
              <span>Visit website</span>
            </a>
          </div>

          {/* User section */}
          <div className="relative border-t border-white/10 p-3">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              className={`
                w-full flex items-center gap-3 rounded-lg p-2 text-left
                transition-colors duration-150
                focus:outline-none focus-visible:ring-1 focus-visible:ring-white/40
                ${userMenuOpen ? 'bg-white/10' : 'hover:bg-white/5'}
              `}
            >
              <MaterialAvatar
                seed={user?.id || user?.email || 'user'}
                name={user?.name || 'User'}
                src={user?.avatarUrl}
                className="h-9 w-9 ring-1 ring-white/20"
                loading="eager"
              />
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-white">
                  {user?.name || 'User'}
                </p>
                <p className="truncate font-mono text-[10px] uppercase tracking-eyebrow text-zinc-500">
                  {planName}
                </p>
              </div>
              <motion.svg
                className="w-4 h-4 shrink-0 text-zinc-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
                animate={{ rotate: userMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 15.75l7.5-7.5 7.5 7.5"
                />
              </motion.svg>
            </button>

            {/*
              Dropdown. Same paper-and-hairline language as the rest of the
              product: an identity header set on paper, ruled items beneath it,
              and log out separated by a full rule rather than coloured red.
            */}
            <AnimatePresence>
              {userMenuOpen && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-10"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <motion.div
                    role="menu"
                    variants={dropdownVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="absolute bottom-full left-3 right-3 z-20 mb-2 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-[0_12px_32px_-12px_rgba(30,34,40,0.35)]"
                  >
                    {/* Identity header — the one place the full email is shown. */}
                    <div className="border-b border-zinc-200 bg-paper-tint px-4 py-3">
                      <p className="truncate text-sm font-medium text-primary">
                        {user?.name || 'User'}
                      </p>
                      <p className="truncate font-mono text-[11px] text-zinc-500">
                        {user?.email}
                      </p>
                    </div>

                    <div className="p-1.5">
                      {userMenuItems.map(item => (
                        <Link
                          key={item.href}
                          to={rebase(item.href)}
                          role="menuitem"
                          onClick={() => {
                            setUserMenuOpen(false);
                            setSidebarOpen(false);
                          }}
                          className="flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-primary transition-colors hover:bg-paper"
                        >
                          <span className="text-zinc-400">{item.icon}</span>
                          {item.name}
                        </Link>
                      ))}
                    </div>

                    <div className="border-t border-zinc-200 p-1.5">
                      <button
                        role="menuitem"
                        onClick={() => {
                          setUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-zinc-600 transition-colors hover:bg-paper hover:text-primary"
                      >
                        <svg
                          className="w-4 h-4 text-zinc-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={1.75}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
                          />
                        </svg>
                        Log out
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div
        className={`${chromeHidden ? '' : 'lg:pl-64'} ${isDemo ? 'pt-10' : ''}`}
      >
        {/* Top bar (mobile) */}
        <header
          className={`sticky ${isDemo ? 'top-10' : 'top-0'} z-30 border-b border-zinc-200 bg-paper/90 backdrop-blur lg:hidden ${chromeHidden ? 'hidden' : ''}`}
        >
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="-ml-2 rounded-lg p-2 text-zinc-500 transition-colors hover:bg-zinc-900/5 hover:text-primary"
              aria-label="Open menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.75}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                />
              </svg>
            </button>
            <div className="flex items-center gap-2">
              <img src="/foundlio-on-light.svg" alt="" className="h-6 w-auto" />
              <span className="font-display font-bold text-primary text-lg tracking-tight">
                Foundlio
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSearchOpen(true)}
                className="rounded-lg p-2 text-zinc-500 transition-colors hover:bg-zinc-900/5 hover:text-primary"
                aria-label="Search"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.75}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
              </button>
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Offline indicator */}
        <OfflineIndicator />

        {/* Page content — paper, with the site's grain over it */}
        <main className="fx-paper min-h-[calc(100vh-4rem)] p-6 lg:min-h-screen lg:p-10">
          <div className="relative z-[1] mx-auto max-w-6xl">
            <VerificationBanner />
            <SectionErrorBoundary name="page">
              <Outlet />
            </SectionErrorBoundary>
          </div>
        </main>
      </div>

      {/* Search Modal */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Keyboard Shortcut Help */}
      <KeyboardShortcutHelp
        isOpen={shortcutHelpOpen}
        onClose={() => setShortcutHelpOpen(false)}
      />
    </div>
  );
};

export default DashboardLayout;
